'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { addMonths, format, isSameDay, startOfMonth, subMonths } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useTRPC } from '@/app/api/trpc/config';
import { MarginsLayout } from '@/frontend/design-system/_marketing/layouts/Margins';
import { Divider } from '@/frontend/design-system/layouts/Divider';
import { VerticalSpacer } from '@/frontend/design-system/layouts/VerticalSpacer';
import { H2 } from '@/frontend/design-system/typography/Heading';
import { P2, P3 } from '@/frontend/design-system/typography/Paragraph';
import { BookingsCalendar } from './_components/BookingsCalendar';
import { LoadingSkeleton } from './_components/LoadingSkeleton';
import { NoCallCard } from './_components/NoCallCard';
import { PlannedCallCard } from './_components/PlannedCallCard';
export function PlannedPhoneCallsSection() {
  const $ = _c(77);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Date();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [selectedDay, setSelectedDay] = useState(t0);
  let t1;
  let t2;
  let t3;
  if ($[1] !== selectedDay) {
    const current = startOfMonth(selectedDay);
    t1 = subMonths(current, 1);
    t2 = current;
    t3 = addMonths(current, 1);
    $[1] = selectedDay;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      prev: t1,
      current: t2,
      next: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const monthsToFetch = t4;
  const trpc = useTRPC();
  let t5;
  if ($[9] !== monthsToFetch.prev || $[10] !== trpc.phoneCallBooking.getAllBookingsForMonth) {
    t5 = trpc.phoneCallBooking.getAllBookingsForMonth.queryOptions({
      startOfMonthDate: monthsToFetch.prev
    });
    $[9] = monthsToFetch.prev;
    $[10] = trpc.phoneCallBooking.getAllBookingsForMonth;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const queryPrev = useQuery(t5);
  let t6;
  if ($[12] !== monthsToFetch.current || $[13] !== trpc.phoneCallBooking.getAllBookingsForMonth) {
    t6 = trpc.phoneCallBooking.getAllBookingsForMonth.queryOptions({
      startOfMonthDate: monthsToFetch.current
    });
    $[12] = monthsToFetch.current;
    $[13] = trpc.phoneCallBooking.getAllBookingsForMonth;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const queryCurrent = useQuery(t6);
  let t7;
  if ($[15] !== monthsToFetch.next || $[16] !== trpc.phoneCallBooking.getAllBookingsForMonth) {
    t7 = trpc.phoneCallBooking.getAllBookingsForMonth.queryOptions({
      startOfMonthDate: monthsToFetch.next
    });
    $[15] = monthsToFetch.next;
    $[16] = trpc.phoneCallBooking.getAllBookingsForMonth;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const queryNext = useQuery(t7);
  let t8;
  if ($[18] !== queryPrev.data?.bookings) {
    t8 = queryPrev.data?.bookings ?? [];
    $[18] = queryPrev.data?.bookings;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== queryCurrent.data?.bookings) {
    t9 = queryCurrent.data?.bookings ?? [];
    $[20] = queryCurrent.data?.bookings;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== queryNext.data?.bookings) {
    t10 = queryNext.data?.bookings ?? [];
    $[22] = queryNext.data?.bookings;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8 || $[26] !== t9) {
    const combined = [...t8, ...t9, ...t10];
    t11 = {
      bookings: combined
    };
    $[24] = t10;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const data = t11;
  const isInitialLoading = queryCurrent.isLoading && data.bookings.length === 0;
  let t12;
  if ($[28] !== data.bookings) {
    t12 = new Set(data.bookings.map(_temp));
    $[28] = data.bookings;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const bookingDays = t12;
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = new Date();
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const now = t13;
  let t14;
  let t15;
  let t16;
  if ($[31] !== data.bookings || $[32] !== selectedDay) {
    let t17;
    if ($[36] !== selectedDay) {
      t17 = booking_0 => isSameDay(new Date(booking_0.startTime), selectedDay);
      $[36] = selectedDay;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    const dayBookings = data.bookings.filter(t17);
    t14 = dayBookings;
    let t18;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = booking_1 => new Date(booking_1.startTime) >= now;
      $[38] = t18;
    } else {
      t18 = $[38];
    }
    t15 = dayBookings.filter(t18);
    let t19;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = booking_2 => new Date(booking_2.startTime) < now;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    t16 = dayBookings.filter(t19);
    $[31] = data.bookings;
    $[32] = selectedDay;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t14 = $[33];
    t15 = $[34];
    t16 = $[35];
  }
  let t17;
  if ($[40] !== t14 || $[41] !== t15 || $[42] !== t16) {
    t17 = {
      dayBookings: t14,
      upcomingBookings: t15,
      pastBookings: t16
    };
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const {
    dayBookings: dayBookings_0,
    upcomingBookings,
    pastBookings
  } = t17;
  if (isInitialLoading) {
    let t18;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <LoadingSkeleton />;
      $[44] = t18;
    } else {
      t18 = $[44];
    }
    return t18;
  }
  let t18;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = newMonth => {
      setSelectedDay(startOfMonth(newMonth));
    };
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== bookingDays || $[47] !== selectedDay) {
    t19 = <section className="self-start lg:sticky lg:top-8"><BookingsCalendar selectedDay={selectedDay} setSelectedDay={setSelectedDay} onMonthChange={t18} bookingDays={bookingDays} /></section>;
    $[46] = bookingDays;
    $[47] = selectedDay;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== dayBookings_0.length) {
    t20 = <H2>Zaplanowane spotkania - {dayBookings_0.length}</H2>;
    $[49] = dayBookings_0.length;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== selectedDay) {
    t21 = format(selectedDay, "dd MMMM yyyy", {
      locale: pl
    });
    $[51] = selectedDay;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t21) {
    t22 = <P2>{t21}</P2>;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t20 || $[56] !== t22) {
    t23 = <header className="flex w-full justify-between gap-3">{t20}{t22}</header>;
    $[55] = t20;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== dayBookings_0.length) {
    t24 = dayBookings_0.length === 0 && <NoCallCard />;
    $[58] = dayBookings_0.length;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== upcomingBookings) {
    t25 = upcomingBookings.map(_temp2);
    $[60] = upcomingBookings;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== t25) {
    t26 = <section className="space-y-4">{t25}</section>;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== pastBookings) {
    t27 = pastBookings.length > 0 && <><Divider /><P3 className="text-contentSecondary">Minione spotkania</P3><section className="space-y-4">{pastBookings.map(_temp3)}</section></>;
    $[64] = pastBookings;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t23 || $[67] !== t24 || $[68] !== t26 || $[69] !== t27) {
    t28 = <main className="w-full space-y-6">{t23}{t24}{t26}{t27}</main>;
    $[66] = t23;
    $[67] = t24;
    $[68] = t26;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== t19 || $[72] !== t28) {
    t29 = <div className="flex flex-col gap-8 lg:flex-row">{t19}{t28}</div>;
    $[71] = t19;
    $[72] = t28;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <VerticalSpacer size="section" />;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t29) {
    t31 = <MarginsLayout>{t29}{t30}</MarginsLayout>;
    $[75] = t29;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  return t31;
}
function _temp3(booking_4) {
  return <PlannedCallCard key={booking_4.scheduleId} booking={booking_4} pastMeeting={true} />;
}
function _temp2(booking_3) {
  return <PlannedCallCard key={booking_3.scheduleId} booking={booking_3} />;
}
function _temp(booking) {
  return format(new Date(booking.startTime), "yyyy.MM.dd");
}